import { c as _c } from "react/compiler-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { Controller, FormProvider, type UseFormReturn, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '~/components/Button';
import { usePathname, useRouter } from '~/i18n/navigation';
import { api } from '~/libs/api';
import { type CreateTripTab, usePlaygroundActions, usePlaygroundContextParams, usePlaygroundCreateTripTab, usePlaygroundUIState } from '~/stores/playgroundStore';
import { type PlaygroundContext, playgroundPlaceSchema } from '~/types/Playground.types';
import { trackEvent } from '~/utils';
import { PlaygroundStartEndSelector } from '../PlaygroundStartEndSelector/PlaygroundStartEndSelector';
import styles from './PlaygroundStartEndForm.module.css';
const ValidationSchema = z.object({
  origin: playgroundPlaceSchema.nullable(),
  endPlace: playgroundPlaceSchema.nullable()
});
type PlaygroundStartEndFormSchemaType = z.infer<typeof ValidationSchema>;
interface PlaygroundStartEndFormProps {
  variant?: 'module' | 'page';
  isHeroCta?: boolean;
  travelType?: CreateTripTab;
}
const BUILD_PATH = '/map/build';
export function PlaygroundStartEndForm({
  travelType = 'round-trip',
  variant = 'page'
}: PlaygroundStartEndFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const {
    setOriginId,
    setIsLoading,
    setContext,
    closeDrawer,
    invalidateTripResults
  } = usePlaygroundActions();
  const createTripTab = usePlaygroundCreateTripTab();
  const {
    chosenOrigin,
    chosenDestination
  } = usePlaygroundContextParams();
  const {
    isLoading
  } = usePlaygroundUIState();
  const playgroundOriginCall = api.playground.selectOriginDestination.useMutation();
  const pathname = usePathname();
  const router = useRouter();
  const onSubmit = async (data: PlaygroundStartEndFormSchemaType) => {
    try {
      setIsLoading(true);
      const {
        origin,
        endPlace
      } = data;
      if (!origin) return;
      setOriginId(origin.id);
      let res: PlaygroundContext | null = null;
      const params = new URLSearchParams();
      if (createTripTab === 'one-way-trip') {
        res = await playgroundOriginCall.mutateAsync({
          originPlaceId: origin?.id ?? null,
          destinationPlaceId: endPlace?.id ?? null,
          playgroundInputContext: null
        });
        params.set('origin', origin.id.toString());
        if (endPlace) {
          params.set('endPlace', endPlace.id.toString());
          trackEvent('end_place_submit', {
            value: endPlace.n,
            event_category: 'Playground',
            event_label: endPlace.n,
            origin_id: origin.id,
            end_place_id: endPlace.id
          });
        }
      } else if (createTripTab === 'round-trip') {
        res = await playgroundOriginCall.mutateAsync({
          originPlaceId: origin?.id ?? null,
          playgroundInputContext: null
        });
        params.set('origin', origin.id.toString());
        trackEvent('origin_submit', {
          value: origin.n,
          event_category: 'Playground',
          event_label: origin.n,
          origin_id: origin.id
        });
      }
      const query = params.toString();
      const nextUrl = query ? `${BUILD_PATH}?${query}` : BUILD_PATH;
      if (res) setContext(res);
      invalidateTripResults();
      if (pathname === BUILD_PATH) {
        if (globalThis.window !== undefined) {
          // Rewrite from the REAL browser path, not `nextUrl`: `pathname` above is next-intl's
          // locale-stripped one, so replaying it into the URL bar drops /en-US from a prefixed
          // build page and reload/share then serves en-GB. Same reasoning as FlexibleDatesPanel.
          const browserPath = globalThis.location.pathname;
          globalThis.history.replaceState(globalThis.history.state, '', query ? `${browserPath}?${query}` : browserPath);
        }
      } else {
        router.replace(nextUrl);
      }
    } catch (_error) {
      // console.error(error)
    } finally {
      setIsLoading(false);
      closeDrawer();
    }
  };
  const playgroundStartEndForm = useForm<PlaygroundStartEndFormSchemaType>({
    resolver: zodResolver(ValidationSchema),
    values: {
      origin: chosenOrigin ?? null,
      endPlace: chosenDestination ?? null
    },
    mode: 'onChange'
  });
  const origin_0 = playgroundStartEndForm.watch('origin');
  const isFormEnabled = Boolean(origin_0?.id);
  return <FormProvider {...playgroundStartEndForm}>
      <form ref={formRef} onSubmit={playgroundStartEndForm.handleSubmit(onSubmit)}>
        <PlaygroundStartEnd variant={variant} originForm={playgroundStartEndForm} isFormEnabled={isFormEnabled} isLoading={isLoading} travelType={travelType} />
      </form>
    </FormProvider>;
}
type PlaygroundStartEndProps = {
  originForm: UseFormReturn<PlaygroundStartEndFormSchemaType>;
  isFormEnabled: boolean;
  variant?: 'module' | 'page';
  isLoading: boolean;
  travelType?: CreateTripTab;
};
export function PlaygroundStartEnd(t0) {
  const $ = _c(39);
  const {
    originForm,
    isFormEnabled,
    variant: t1,
    isLoading,
    travelType: t2
  } = t0;
  const variant = t1 === undefined ? "page" : t1;
  const travelType = t2 === undefined ? "round-trip" : t2;
  const isModule = variant === "module";
  const t = useTranslations("playground");
  let t3;
  if ($[0] !== originForm.control) {
    t3 = {
      control: originForm.control,
      name: "origin"
    };
    $[0] = originForm.control;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const origin = useWatch(t3);
  let t4;
  if ($[2] !== originForm.control) {
    t4 = {
      control: originForm.control,
      name: "endPlace"
    };
    $[2] = originForm.control;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const endPlace = useWatch(t4);
  let t5;
  if ($[4] !== isModule) {
    t5 = clsx(styles.contentAndCta, {
      [styles.moduleVariant]: isModule
    });
    $[4] = isModule;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isModule) {
    t6 = clsx(styles.heading, {
      [styles.moduleVariant]: isModule
    });
    $[6] = isModule;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t || $[9] !== travelType) {
    t7 = travelType === "round-trip" ? t("origin_heading") : t("origin_and_destination_heading");
    $[8] = t;
    $[9] = travelType;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <h1 className={t6}>{t7}</h1>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== isModule || $[15] !== t) {
    t9 = !isModule ? <p className={styles.body}>{t("origin_change_description")}</p> : <p className={styles.body}>{t("origin_change_warning")}</p>;
    $[14] = isModule;
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== originForm.control) {
    t10 = <Controller control={originForm.control} name="origin" render={_temp} />;
    $[17] = originForm.control;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== originForm.control || $[20] !== travelType) {
    t11 = travelType === "one-way-trip" && <Controller control={originForm.control} name="endPlace" render={_temp2} />;
    $[19] = originForm.control;
    $[20] = travelType;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className={styles.selectors}>{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t8 || $[27] !== t9) {
    t13 = <div className={styles.content}>{t8}{t9}{t12}</div>;
    $[25] = t12;
    $[26] = t8;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const t14 = !isFormEnabled || !origin || travelType === "one-way-trip" && !endPlace;
  let t15;
  if ($[29] !== t) {
    t15 = t("trip_update");
    $[29] = t;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== isLoading || $[32] !== t14 || $[33] !== t15) {
    t16 = <div className={styles.bottomButtonBar}><Button isDisabled={t14} type="submit" isLoading={isLoading}>{t15}</Button></div>;
    $[31] = isLoading;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t13 || $[36] !== t16 || $[37] !== t5) {
    t17 = <div className={t5}>{t13}{t16}</div>;
    $[35] = t13;
    $[36] = t16;
    $[37] = t5;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
}
function _temp2(t0) {
  const {
    field: field_0
  } = t0;
  return <PlaygroundStartEndSelector variant="end" {...field_0} />;
}
function _temp(t0) {
  const {
    field
  } = t0;
  return <PlaygroundStartEndSelector variant="start" {...field} />;
}